import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * Query key convention: the first segment is the owner.
 *   core:     ["me"], ["ig-accounts"] (+ ["ig-accounts", "overview"]), ["settings", igAccountId | "user"],
 *             ["reseller", …], ["admin", …]
 *   features: ["<feature>", …]   e.g. ["crm", "board", igAccountId]
 * Workspaces: anything that belongs to an Instagram account carries its id in the key, so switching
 * accounts refetches (lib/workspace.ts).
 * Invalidate a whole feature with queryClient.invalidateQueries({ queryKey: ["crm"] }).
 */
export function createQueryClient() {
  // The plan changed under us (a reseller moved this account to a smaller plan): refresh /me so the
  // portal locks the page instead of showing errors (lib/plan.ts, docs/PLANS.md).
  const onError = (error: unknown) => {
    if (error instanceof ApiError && error.code === "feature_locked") void client.invalidateQueries({ queryKey: ["me"] });
  };
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          // Client errors won't fix themselves; network blips and 5xx get two more tries.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
  return client;
}
